import React from 'react';
import { Box, Button, Container, Link, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PageMeta from '../components/PageMeta';

type PageName = 'about' | 'privacy' | 'terms';

const updated = 'August 1, 2026';

export default function InfoPage({ page }: { page: PageName }) {
  const navigate = useNavigate();

  return (
    <Container maxWidth="md">
      <PageMeta
        title={`${page === 'about' ? 'About' : page === 'privacy' ? 'Privacy & Cookies' : 'Terms'} | Anime Opening Quiz`}
        description={`${page === 'about' ? 'About Anime Opening Quiz.' : page === 'privacy' ? 'Privacy and cookie information for Anime Opening Quiz.' : 'Terms for Anime Opening Quiz.'}`}
        path={`/${page}`}
        index={page === 'about'}
      />
      <Box sx={{ py: 4 }}>
        <Button onClick={() => navigate('/')} sx={{ mb: 2 }}>← Back to quiz</Button>

        {page === 'about' && (
          <>
            <Typography variant="h3" component="h1">About</Typography>
            <Typography paragraph>
              Anime Opening Quiz is a free fan-made game where players identify anime
              series from short opening clips. It offers single-player and multiplayer modes.
            </Typography>
            <Typography paragraph>
              The site is independently operated and is not affiliated with or endorsed by
              YouTube, Google, anime studios, publishers or rights holders. Video playback is
              provided through YouTube's embedded player.
            </Typography>
            <Typography paragraph>
              Questions, corrections or removal requests can be sent to{' '}
              <Link href="mailto:mediumfungames@gmail.com">mediumfungames@gmail.com</Link>.
            </Typography>
          </>
        )}

        {page === 'privacy' && (
          <>
            <Typography variant="h3" component="h1">Privacy & Cookies</Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Last updated: {updated}
            </Typography>
            <Typography paragraph>
              We do not require accounts and do not intentionally collect names, addresses or
              payment information. Temporary game data such as room names, guesses and scores
              is processed in memory and is removed when rooms end or the server restarts.
            </Typography>
            <Typography paragraph>
              Our server may process standard technical data such as IP addresses, request
              times and browser information for security, rate limiting and operational logs.
            </Typography>
            <Typography paragraph>
              YouTube embeds may receive technical data and use cookies or similar storage
              under Google's policies when a video is loaded. If advertising is enabled later,
              Google and its partners may also use cookies for ad delivery and measurement.
              Where required, a consent message will be shown before such advertising cookies
              are used.
            </Typography>
            <Typography paragraph>
              The site may use local browser storage to remember basic game preferences. You
              can clear this through your browser settings. Privacy questions can be sent to{' '}
              <Link href="mailto:mediumfungames@gmail.com">mediumfungames@gmail.com</Link>.
            </Typography>
          </>
        )}

        {page === 'terms' && (
          <>
            <Typography variant="h3" component="h1">Terms</Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Last updated: {updated}
            </Typography>
            <Typography paragraph>
              Anime Opening Quiz is provided free of charge for entertainment. Do not misuse
              the service, disrupt games, automate excessive requests or attempt to access the
              server or other users' systems.
            </Typography>
            <Typography paragraph>
              Availability and quiz content may change without notice. We do not guarantee
              uninterrupted operation or the accuracy of every title, clip or difficulty rating.
            </Typography>
            <Typography paragraph>
              All third-party names, trademarks and media remain the property of their
              respective owners. For content concerns, contact{' '}
              <Link href="mailto:mediumfungames@gmail.com">mediumfungames@gmail.com</Link>.
            </Typography>
          </>
        )}
      </Box>
    </Container>
  );
}
